import { commandAddresseeCode, type CommandEntryDto, type DutyPointDto, type OfficerSummaryDto } from '@pams/shared';
import { PREVIEW_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C6a] A ROLE ASKED OF A DIVISION READS WHERE EACH POSITION STANDS — NEVER HOW MANY.
 *
 *  The order's creator asked the division; the division names the officers, or hands a position to
 *  one of its units. So this row reads from `asks[].seat` — who holds each position NOW — and never
 *  from `officerIds`/`officers`, which stay the creator's by-name choice and are empty on an asked
 *  role (`commandSeat.schema.ts`: a division's pick is never written back there).
 *
 *    · a position the division has answered — the officer, exactly as a by-name officer reads:
 *      rank, name, and the number as a `tel:` link;
 *    · one still awaited — `Awaited from CD` (`CD · TALA` for a unit asked directly);
 *    · one the division handed to a unit that has not answered — `Handed to TALA · awaited`;
 *    · a DRAFT has no seats yet — `Asked of CD`;
 *    · on an order that is over or withdrawn (`live` false), an empty position — awaited or handed
 *      down alike — reads `Not named by CD` (`CD · TALA` for a unit), never "awaited".
 *
 *  ⚠️ `live` IS THE HOST'S `awaitedIsLive(order)`, REQUIRED AND NEVER DEFAULTED. The seat carries no
 *  naming window, and the server's other readers of it — the printed order and the bell — say
 *  "awaited" only while a name can still be given and never for an archived order; a default here
 *  would put the promise in the omitted position. `commandWords.ts` states the gap that remains.
 *
 *  ⚠️ EACH PHRASE LISTS DISTINCT CODES, SO TWO EMPTY POSITIONS ASKED OF CENTRAL READ `Awaited from
 *  CD` ONCE. Two identical phrases side by side would be a count spelled as repetition — FR-5.2.6b
 *  forbids a command number in any costume, and the tests run the no-number claim over this block.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
type AskedWords = {
  officers: OfficerSummaryDto[];
  asked: string[];
  awaited: string[];
  handed: string[];
  unnamed: string[];
};

const askedWords = (asks: NonNullable<CommandEntryDto['asks']>, live: boolean): AskedWords => {
  const words: AskedWords = { officers: [], asked: [], awaited: [], handed: [], unnamed: [] };
  const once = (list: string[], code: string): void => {
    if (!list.includes(code)) list.push(code);
  };
  /* Over or withdrawn, an empty position names who held it, in the printed order's codes —
     `CD · TALA` once handed down. */
  for (const ask of asks) {
    const seat = ask.seat;
    if (seat === null) once(words.asked, commandAddresseeCode(ask.division, ask.unit));
    else if (seat.officer !== null) words.officers.push(seat.officer);
    else if (!live)
      once(words.unnamed, commandAddresseeCode(ask.division, seat.handedDown ? seat.addressee : ask.unit));
    else if (seat.handedDown) once(words.handed, seat.addressee.shortCode);
    else once(words.awaited, commandAddresseeCode(ask.division, ask.unit));
  }
  return words;
};

/* The by-name shape and the answered seat's are one rendering: rank, name, a `tel:` number. */
const OfficerContact = ({ officer, first }: { officer: OfficerSummaryDto; first: boolean }) => (
  <span>
    {first ? '' : ', '}
    {officer.rankShortName ? `${officer.rankShortName} ` : ''}
    {officer.name}{' '}
    {/* A dashboard is used beside a desk phone; a `tel:` costs nothing
        where it does not resolve. */}
    <a className="text-link" href={`tel:${officer.phone}`}>
      {officer.phone}
    </a>
  </span>
);

const AskedValue = ({ asks, live }: { asks: NonNullable<CommandEntryDto['asks']>; live: boolean }) => {
  const words = askedWords(asks, live);
  const phrases = [
    words.awaited[0] === undefined ? null : PREVIEW_COPY.awaitedFrom(words.awaited),
    words.handed[0] === undefined ? null : PREVIEW_COPY.handedTo(words.handed),
    words.unnamed[0] === undefined ? null : PREVIEW_COPY.notNamedBy(words.unnamed),
    words.asked[0] === undefined ? null : PREVIEW_COPY.askedOf(words.asked),
  ].filter((phrase): phrase is string => phrase !== null);
  return (
    <>
      {words.officers.map((officer, at) => (
        <OfficerContact key={officer.id} officer={officer} first={at === 0} />
      ))}
      {phrases.map((phrase, at) => (
        <span key={phrase} className="text-muted-foreground" data-testid="preview-command-asked">
          {words.officers[0] === undefined && at === 0 ? '' : ', '}
          {phrase}
        </span>
      ))}
    </>
  );
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1h §4, extracted C3a §4e] WHO IS IN CHARGE — NAMES **AND** PHONE NUMBERS, INLINE.
 *
 *  ⚠️ [amended C1f §2b] NAMES AND PHONES, UNCOUNTED AND WITH NO REVEAL STEP. [P5a] settled the
 *  split structurally — a FORCE officer's number is absent from the payload entirely, a
 *  COMMANDER's is inline — and [C1b §5c] confirmed no meter and no reveal for the second. This
 *  DISCLOSES NOTHING NEW: `?view=record` already returns these exact bytes to every
 *  `arrangement.read` holder in scope — and the printed order carries them too since [C5 §7b].
 *
 *  ⚠️ NAMED, NEVER COUNTED — FR-5.2.6b. No number may appear beside one of these rows, which is
 *  why the force grid and this block are rendered separately rather than sharing a row
 *  component. `commandRoleIsolation.test.ts` CLAIM 5 scans `api/src` and `packages/shared/src`
 *  rather than `web/`, so the rule here is held by review — and by this sentence.
 *
 *  ⚠️ [C3a §4e] EXTRACTED SO §4's PAGE AND THE PREVIEW MODAL RENDER THE SAME BLOCK. A second
 *  copy would be a second answer to "does a commander's number show", which is exactly the
 *  question [P5a] spent a pass making structural.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const CommandContacts = ({ point, live }: { point: DutyPointDto; live: boolean }): React.ReactElement => (
  <div className="flex flex-col gap-0.5" data-testid={`preview-command-${point.id}`}>
    {point.commandEntries.map((entry, index) => (
      <div
        key={`${entry.roleKey ?? entry.adHocLabel ?? ''}-${String(index)}`}
        /*
         * ⚠️ [C3b §3.1] LEFT-ALIGNED, NOT SPLIT ACROSS THE CARD. This was
         * `justify-between` with a `text-right` value, so "Incharge" sat at one edge and the
         * names at the other with a lake of whitespace between them — on a wide card the two
         * halves of one fact stopped reading as one fact at all. The role now takes a fixed
         * measure and the names begin immediately after it, which is the "Incharge <tab>
         * [Names, Number]" shape that was asked for.
         *
         * ⚠️ A FIXED `w-32` RATHER THAN `justify-between`, because the roles must line up with
         * EACH OTHER — a shrink-to-fit label would start the names at a different x on every
         * row. `shrink-0` on the label and `min-w-0` on the value is what lets the NAMES wrap
         * and truncate rather than the role.
         */
        className="flex items-baseline gap-3 text-xs"
      >
        <span className="w-32 shrink-0 truncate text-muted-foreground">
          {entry.roleLabel ?? entry.adHocLabel ?? ''}
        </span>
        <span className="min-w-0 text-foreground">
          {/* ⚠️ [C6a] An asked role reads its positions — never `officers`, which is empty on it. */}
          {entry.asks?.[0] !== undefined ? (
            <AskedValue asks={entry.asks} live={live} />
          ) : entry.officers.length === 0 ? (
            PREVIEW_COPY.noCommandNamed
          ) : (
            entry.officers.map((officer, at) => <OfficerContact key={officer.id} officer={officer} first={at === 0} />)
          )}
        </span>
      </div>
    ))}
  </div>
);
